import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, RefreshCw, Loader2, Info,
  AlertTriangle, AlertOctagon, ArrowLeft, ExternalLink,
} from "lucide-react";

/** Per-employee compliance scorecard. Computed against current KeyPay
 *  profile data + (when wired) FWC MAPD minimum-wage check. */
export default function KeyPayCompliancePage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/integrations/keypay/clients/${clientId}/compliance-scorecard`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load scorecard");
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-[1280px]" data-testid="keypay-compliance-page">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to KeyPay client
      </Link>

      <div className="flex items-start gap-3 mb-5 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-800">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="label-overline">FINANCE &amp; PAYROLL · KEYPAY · COMPLIANCE</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Compliance scorecard</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Per-employee compliance signals computed from KeyPay profile data.
            Missing TFN, missing super, casual-conversion eligibility, etc.
            Live FWC Modern Award minimum-wage checks unlock when the FWC API
            key is configured on the
            <Link to="/fairwork" className="text-amber-800 underline mx-1">Fair Work page<ExternalLink className="h-3 w-3 inline" /></Link>.
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm h-7"
          onClick={load} disabled={loading} data-testid="keypay-compliance-reload">
          {loading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                   : <RefreshCw className="h-3 w-3 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {!data ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin inline" />
        </CardContent></Card>
      ) : (
        <>
          {/* Roll-up */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5"
            data-testid="keypay-compliance-summary">
            {[
              { k: "critical", label: "Critical", c: "bg-red-50 text-red-800 border-red-200" },
              { k: "warn", label: "Warnings", c: "bg-amber-50 text-amber-800 border-amber-200" },
              { k: "info", label: "Info", c: "bg-blue-50 text-blue-800 border-blue-200" },
              { k: "ok", label: "OK", c: "bg-emerald-50 text-emerald-800 border-emerald-200" },
            ].map((s) => (
              <div key={s.k} className={`border rounded-sm p-3 ${s.c}`}
                data-testid={`keypay-compliance-stat-${s.k}`}>
                <div className="label-overline text-[9px]">{s.label}</div>
                <div className="font-display font-bold text-2xl mt-0.5">
                  {data.by_risk_level?.[s.k] ?? 0}
                </div>
              </div>
            ))}
            <div className={`border rounded-sm p-3 ${
              data.fwc_api_live ? "bg-emerald-50 border-emerald-200"
                                : "bg-zinc-50 border-zinc-200"}`}>
              <div className="label-overline text-[9px]">FWC MAPD API</div>
              <div className="font-display font-bold text-base mt-0.5">
                {data.fwc_api_live ? "Live ✓" : "Pending"}
              </div>
            </div>
          </div>

          {!data.fwc_api_live && (
            <div className="mb-4 p-3 border border-amber-200 rounded-sm bg-amber-50 text-xs text-amber-900">
              <Info className="h-3 w-3 inline mr-1" />
              {data.fwc_api_status_message}
            </div>
          )}

          {/* Rows */}
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-0">
              <div className="grid grid-cols-[1fr_140px_120px_90px_120px] gap-2 px-3 py-2 bg-zinc-50 border-b text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700">
                <span>Employee</span><span>Type</span><span>Classification</span>
                <span className="text-right">Rate</span><span>Risk</span>
              </div>
              {(data.employees || []).map((e) => (
                <div key={e.employee_id}
                  className="border-b last:border-b-0 px-3 py-2"
                  data-testid={`keypay-compliance-row-${e.employee_id}`}>
                  <div className="grid grid-cols-[1fr_140px_120px_90px_120px] gap-2 text-sm items-center">
                    <span className="font-semibold">{e.name}
                      {e.job_title && <span className="text-[10px] text-zinc-500 font-mono ml-2">{e.job_title}</span>}
                    </span>
                    <span className="text-xs font-mono">{e.employment_type || "—"}</span>
                    <span className="text-[11px] text-zinc-600 truncate" title={e.classification}>
                      {e.classification || <em className="text-zinc-400">none</em>}
                    </span>
                    <span className="text-right font-mono text-[11px]">
                      {e.pay_rate ? `$${e.pay_rate.toFixed(2)}` : "—"}
                    </span>
                    <RiskBadge level={e.risk_level} />
                  </div>
                  {e.flags.length > 0 && (
                    <div className="mt-1.5 grid gap-1">
                      {e.flags.map((f) => (
                        <div key={f.code}
                          className={`text-[11px] flex items-start gap-1.5 px-2 py-1 rounded-sm ${flagColor(f.severity)}`}>
                          <FlagIcon severity={f.severity} />
                          <span><strong className="font-mono uppercase text-[9px] tracking-wider mr-1">{f.code}</strong>{f.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {e.fwc_minimum_check === "pending_api" && data.fwc_api_live === false && (
                    <div className="mt-1 text-[10px] text-zinc-400 italic">
                      FWC minimum-wage check pending API connection.
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function RiskBadge({ level }) {
  const cfg = {
    critical: ["bg-red-100 text-red-800 border-red-200", "Critical"],
    warn: ["bg-amber-100 text-amber-800 border-amber-200", "Warn"],
    info: ["bg-blue-100 text-blue-800 border-blue-200", "Info"],
    ok: ["bg-emerald-100 text-emerald-800 border-emerald-200", "OK"],
  }[level] || ["bg-zinc-100", level];
  return (
    <Badge variant="outline" className={`rounded-sm text-[9px] font-display font-bold uppercase tracking-[0.12em] ${cfg[0]}`}>
      {cfg[1]}
    </Badge>
  );
}
function flagColor(sev) {
  return { critical: "bg-red-50 text-red-800",
           warn: "bg-amber-50 text-amber-900",
           info: "bg-blue-50 text-blue-900",
           ok: "bg-emerald-50 text-emerald-900" }[sev] || "bg-zinc-50";
}
function FlagIcon({ severity }) {
  const Icon = severity === "critical" ? AlertOctagon
             : severity === "warn" ? AlertTriangle
             : severity === "info" ? Info : ShieldAlert;
  return <Icon className="h-3 w-3 shrink-0 mt-0.5" />;
}
